// 모션 훅: 드로우온(stroke-dashoffset) + 점수 카운트업. (디자인 핸드오프 모션 스펙)

import { useEffect, useRef, useState } from "react";

/**
 * 드로우온: ref 안의 [data-draw] 요소들을 stroke-dashoffset로 그려낸다.
 * dur ~ 전체 시간(ms), stagger로 요소별 지연.
 */
export function useDrawOn<T extends SVGElement>(active: boolean, dur = 1100) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!active || !ref.current) return;
    let els = Array.from(ref.current.querySelectorAll<SVGGeometryElement>("[data-draw]"));
    if (els.length === 0) {
      els = Array.from(ref.current.querySelectorAll<SVGGeometryElement>("path,line,circle,polyline,polygon"));
    }
    const n = els.length || 1;
    els.forEach((el) => {
      let len = 200;
      try {
        len = el.getTotalLength() || 200;
      } catch {
        /* noop */
      }
      el.style.transition = "none";
      el.style.strokeDasharray = String(len);
      el.style.strokeDashoffset = String(len);
    });
    void ref.current.getBoundingClientRect();
    const raf = requestAnimationFrame(() => {
      els.forEach((el, i) => {
        const delay = (i / n) * dur * 0.55;
        el.style.transition = `stroke-dashoffset ${(dur * 0.6).toFixed(0)}ms cubic-bezier(.6,.02,.3,1) ${delay.toFixed(0)}ms`;
        el.style.strokeDashoffset = "0";
      });
    });
    const fb = setTimeout(() => els.forEach((el) => (el.style.strokeDashoffset = "0")), dur + 700);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fb);
    };
  }, [active, dur]);
  return ref;
}

function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}

/** 점수 카운트업: 0 → target, easeOutCubic. */
export function useCountUp(target: number, ms = 1100): number {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf = 0;
    let start = 0;
    const tick = (ts: number) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / ms);
      setVal(Math.round(easeOutCubic(p) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return val;
}
